package com.bitterinfant.webui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.bitterinfant.shared.MediaItem
import com.bitterinfant.shared.MediaKind
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.document
import org.w3c.dom.HTMLAudioElement
import org.w3c.dom.HTMLMediaElement
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.asList

/** Pauses every other <audio>/<video> on the page, so only one plays at a time. */
private fun pauseOtherMedia(current: HTMLMediaElement) {
    document.querySelectorAll("audio, video").asList()
        .filterIsInstance<HTMLMediaElement>()
        .filter { it !== current && !it.paused }
        .forEach { it.pause() }
}

/**
 * Renders a native <video> element with multiple <source> tags so the browser
 * picks whichever format it supports (put webm/av1 first, mp4/h264 as the safe fallback).
 * Falls back to a poster image + download link if nothing plays.
 * [anchor] overrides the slugified title as this clip's `?play=` deep-link name (see [DeepLink]);
 * the caption carries a [CopyLinkButton] for it.
 */
@Composable
fun VideoPlayer(item: MediaItem, anchor: String? = null, modifier: (AttrsScope<HTMLVideoElement>.() -> Unit)? = null) {
    val sources = item.sources.filter { it.kind == MediaKind.VIDEO }
    val anchorId = slugify(anchor ?: item.title)
    val isTarget = remember { DeepLink.claim(anchorId) }
    Div(attrs = { classes("media-block"); if (anchorId.isNotEmpty()) id(anchorId) }) {
        Video(attrs = {
            if (isTarget) ref { playDeepLinkTarget(it); onDispose {} }
            attr("controls", "true")
            attr("playsinline", "true")
            attr("preload", "metadata")
            addEventListener("play") { pauseOtherMedia(it.target as HTMLMediaElement) }
            item.posterPath?.let { attr("poster", it) }
            style {
                width(100.percent)
                maxWidth(Theme.maxWidth)
                property("border-radius", "8px")
                property("background", Theme.bgElevated)
            }
            modifier?.invoke(this)
        }) {
            sources.forEach { src ->
                Source(attrs = {
                    attr("src", src.path)
                    attr("type", src.mimeType)
                })
            }
            Text("Your browser doesn't support embedded video. ")
            sources.firstOrNull()?.let { A(href = it.path) { Text("Download it here.") } }
        }
        if (item.title.isNotBlank()) Caption(item.title, item.description, anchorId)
    }
}

/**
 * Renders a native <audio> element with multiple sources (e.g. mp3 + ogg/aac fallback).
 * Downloads are discouraged: the browser's download menu item and the right-click menu are
 * disabled and there's no fallback download link. Starting one player pauses any other.
 * [tooltip], if set, renders as a tap/hover info bubble (see [TooltipIcon]) next to the title
 * instead of [note]'s always-visible subtitle line — handy when you want a description
 * available without it taking up space on the page. Works on both mouse and touch devices,
 * unlike the native `title` attribute.
 * [anchor] overrides the slugified title as this track's `?play=` deep-link name (see [DeepLink]);
 * the title row carries a [CopyLinkButton] for it.
 */
@Composable
fun AudioPlayer(
    item: MediaItem,
    note: String? = null,
    tooltip: String? = null,
    anchor: String? = null,
    modifier: (AttrsScope<HTMLAudioElement>.() -> Unit)? = null,
) {
    val sources = item.sources.filter { it.kind == MediaKind.AUDIO }
    val anchorId = slugify(anchor ?: item.title)
    val isTarget = remember { DeepLink.claim(anchorId) }
    Div(attrs = { classes("media-block"); if (anchorId.isNotEmpty()) id(anchorId) }) {
        if (item.title.isNotBlank() || tooltip != null || anchorId.isNotEmpty()) {
            P(attrs = {
                style {
                    color(Color(Theme.textMuted)); marginTop(0.px); marginBottom(0.5.cssRem)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                }
            }) {
                B { Text(item.title) }
                if (item.description.isNotBlank()) Text(" — ${item.description}")
                tooltip?.let { TooltipIcon(it) }
                if (anchorId.isNotEmpty()) CopyLinkButton(anchorId)
            }
        }
        if (!note.isNullOrBlank()) {
            P(attrs = { style { color(Color(Theme.textMuted)); fontSize(0.8.cssRem); marginTop(0.px); marginBottom(0.5.cssRem); marginLeft(0.8.cssRem) } }) {
                Text(note)
            }
        }
        Audio(attrs = {
            if (isTarget) ref { playDeepLinkTarget(it); onDispose {} }
            attr("controls", "true")
            attr("controlslist", "nodownload")
            attr("preload", "metadata")
            addEventListener("contextmenu") { it.preventDefault() }
            addEventListener("play") { pauseOtherMedia(it.target as HTMLMediaElement) }
            style { width(100.percent); maxWidth(Theme.maxWidth) }
            modifier?.invoke(this)
        }) {
            sources.forEach { src ->
                Source(attrs = {
                    attr("src", src.path)
                    attr("type", src.mimeType)
                })
            }
            Text("Your browser doesn't support embedded audio.")
        }
    }
}

/**
 * Embeds a responsive, centered YouTube video (16:9 aspect ratio) via the standard embed URL.
 * Pass just the video ID (the part after `v=` in a youtube.com/watch URL). [caption], if set,
 * renders as a visible title above the video (and doubles as the iframe's accessible title).
 * [captionUrl], if set, turns that caption into a link (e.g. to where the full work can be
 * watched) opening in a new tab. [tooltip], if set, overlays a tap/hover info icon (see
 * [TooltipIcon]) in the video's top-right corner. [anchor] overrides the slugified caption (or
 * the video ID, if there's no caption) as this video's `?play=` deep-link name (see [DeepLink]);
 * a deep-linked video is embedded with YouTube's `autoplay=1` (and `start=` for a `&t=` link).
 * Its title row also carries a [CopyLinkButton] (whose links can't include the current position,
 * since the YouTube player's time isn't readable from this page; add `&t=` by hand).
 */
@Composable
fun YouTubeEmbed(videoId: String, caption: String? = null, captionUrl: String? = null, tooltip: String? = null, anchor: String? = null) {
    val anchorId = slugify(anchor ?: caption ?: videoId)
    val isTarget = remember { DeepLink.claim(anchorId) }
    Div(attrs = {
        classes("media-block")
        if (anchorId.isNotEmpty()) id(anchorId)
        if (isTarget) ref { revealDeepLinkTarget(it); onDispose {} }
        style {
            maxWidth(Theme.maxWidth)
            property("margin", "0 auto 2rem")
        }
    }) {
        P(attrs = {
            style {
                color(Color(Theme.textMuted)); marginTop(0.px); marginBottom(0.5.cssRem)
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
            }
        }) {
            caption?.let {
                if (captionUrl != null) {
                    A(href = captionUrl, attrs = {
                        attr("target", "_blank")
                        style { color(Color(Theme.textMuted)) }
                    }) { B { Text(it) } }
                } else {
                    B { Text(it) }
                }
            }
            if (anchorId.isNotEmpty()) CopyLinkButton(anchorId)
        }
        Div(attrs = {
            style {
                position(Position.Relative)
                width(100.percent)
                property("padding-top", "56.25%")
                property("border-radius", "8px")
                property("overflow", "hidden")
                property("background", Theme.bgElevated)
            }
        }) {
            Iframe(attrs = {
                val start = DeepLink.startSeconds?.let { "&start=$it" } ?: ""
                attr("src", "https://www.youtube.com/embed/$videoId" + if (isTarget) "?autoplay=1$start" else "")
                attr("title", caption ?: "YouTube video player")
                attr("frameborder", "0")
                attr("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share")
                attr("allowfullscreen", "true")
                style {
                    position(Position.Absolute)
                    property("top", "0")
                    property("left", "0")
                    width(100.percent)
                    height(100.percent)
                    property("border", "0")
                }
            })
            tooltip?.let {
                Div(attrs = {
                    style {
                        position(Position.Absolute)
                        top(0.5.cssRem)
                        right(0.5.cssRem)
                        property("z-index", "20")
                    }
                }) { TooltipIcon(it, alignEnd = true) }
            }
        }
    }
}

/** Responsive <img> with lazy loading and an async decode hint; pass 2x/3x variants via srcset. */
@Composable
fun ResponsiveImage(src: String, alt: String, srcset: String? = null, sizes: String = "100vw") {
    Img(src = src, alt = alt, attrs = {
        attr("loading", "lazy")
        attr("decoding", "async")
        srcset?.let { attr("srcset", it) }
        attr("sizes", sizes)
        style {
            width(100.percent)
            maxWidth(Theme.maxWidth)
            property("border-radius", "8px")
            property("display", "block")
        }
    })
}

@Composable
private fun Caption(title: String, description: String, anchor: String) {
    P(attrs = { style { color(Color(Theme.textMuted)); marginTop(0.5.cssRem); display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
        B { Text(title) }
        if (description.isNotBlank()) Text(" — $description")
        CopyLinkButton(anchor)
    }
}
